import clsx from 'clsx';
import React, { createContext } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useFetchUserQuery } from '@/api/globalApis';
import { DialogsContainer, Drawer, TopBar } from '@/components';
import { menuConfig } from '@/config/menuConfig';
import { SIDE_NAVIGATION_MENU } from '@/globalConsts';
import { useAllowedToView, useDisclosure, useUserRole } from '@/hooks';

export interface MainLayoutProps {
	children: React.ReactNode;
}

type DrawerContextType = {
	isOpen: boolean;
};

export const DrawerContext = createContext<DrawerContextType>({} as DrawerContextType);

export const MainLayout = (props: MainLayoutProps) => {
	const navigate = useNavigate();
	const { close, isOpen, open } = useDisclosure();
	const { data: userData } = useFetchUserQuery();
	const userFeatures = userData?.features;
	const userRole = useUserRole(userFeatures);
	const currentMenuConfig = menuConfig[userRole as keyof typeof menuConfig];

	// Check if the current page is in the menuConfig before rendering the layout. If it's not, redirect to default page.
	const allowedToView = useAllowedToView(currentMenuConfig, useLocation().pathname);
	if (!allowedToView) {
		navigate('/', { replace: true });
		return null;
	}

	const { children } = props;
	const contextValue = { isOpen };

	return (
		<DrawerContext.Provider value={contextValue}>
			<main
				className={clsx(
					'pt-20 outer min-w-screen',
					isOpen
						? `${SIDE_NAVIGATION_MENU.PADDING_LEFT.EXPANDED} transition-width delay-100 duration-300 ease-in-out`
						: `${SIDE_NAVIGATION_MENU.PADDING_LEFT.DEFAULT} transition-width delay-100 duration-300 ease-in-out`
				)}
			>
				<TopBar isCollapsed={isOpen} />
				<Drawer
					config={menuConfig}
					close={close}
					isOpen={isOpen}
					open={open}
					className='fixed top-0 left-0'
					userRole={userRole}
				/>
				{children}
				<DialogsContainer />
			</main>
		</DrawerContext.Provider>
	);
};
